import { createContext, useContext, useEffect, type ReactNode } from "react";

import type { ExtendedVisionUser } from "@thalamos/vision-common";
import { ExtendedVisionUserSchema } from "@thalamos/vision-common";

import api, { apiHooks } from "../api/index.js";
import { Error as ErrorComponent } from "../pages/Error/Error.js";
import { setUser } from "../tracing.js";

export type UserContextType = {
  user: ExtendedVisionUser | undefined;
  loading: boolean;
  error: Error | null;
  refetchUser: () => Promise<unknown>;
};

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

let loginCheckInterval: ReturnType<typeof setInterval> | undefined = undefined;

const WithLoginCheck = ({
  children,
  user,
}: {
  children: ReactNode;
  user: ExtendedVisionUser | undefined;
}) => {
  // We ping /api/health/authed-ping every 10 seconds to check that the session is still active.
  // Any API request _other_ than /api/health/authed-ping will refresh the session, so this
  // is essentially our way of doing session inactivity timeouts
  const CHECK_LOGIN_STATUS_INTERVAL_MS = 1000 * 10;
  useEffect(() => {
    if (loginCheckInterval) {
      clearInterval(loginCheckInterval);
    }
    if (user) {
      loginCheckInterval = setInterval(() => {
        void api.user.loggedInCheck();
      }, CHECK_LOGIN_STATUS_INTERVAL_MS);
    }
  }, [user]);

  // Set user for Datadog tracing
  useEffect(() => {
    if (user) {
      setUser(user);
    }
  }, [user]);

  return <>{children}</>;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [{ data, loading, response, error }, refetchUser] = apiHooks.user.me();

  let user: UserContextType["user"] = undefined;

  try {
    if (response?.status === 200 && data !== undefined) {
      user = ExtendedVisionUserSchema.parse(data);
    }
  } catch (parseError) {
    console.error("Error parsing user data:", parseError);
    return <ErrorComponent />;
  }

  return (
    <UserContext.Provider value={{ user, loading, error, refetchUser }}>
      <WithLoginCheck user={user}>{children}</WithLoginCheck>
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
